import fr from 'date-fns/locale/fr';
import React, { useEffect, useState } from 'react';
import { registerLocale } from 'react-datepicker';
import { RiMegaphoneFill } from 'react-icons/ri';
import { TbDeviceAnalytics, TbInfoCircle } from 'react-icons/tb';
import { Link, useNavigate, useParams } from 'react-router-dom';

import 'react-datepicker/dist/react-datepicker.css';
import { useFetchRestaurantQuery } from '@/api/hooks/useRestaurant';
import { useFetchKPIRestaurantMutation, useFetchPeriodStatsRestaurantMutation } from '@/api/hooks/useSubscription';
import { RestaurantKPIResponse } from '@/api/lib/subscription';

import { Typography } from '@/components/atoms/Typography';
import { AdInfos } from '@/components/molecules/AdInfos';
import AdStatsContainer from '@/components/molecules/AdStatsContainer/AdStatsContainer';
import { BaseModal } from '@/components/molecules/BaseModal';
import { KPIDoubleSection } from '@/components/molecules/KPIDoubleSection';
import { KPISection } from '@/components/molecules/KPISection';
import MonthsDaysSelector, { months } from '@/components/molecules/MonthsDaysSelector/MonthsDaysSelector';
import RestaurantCharts from '@/components/molecules/RestaurantCharts/RestaurantCharts';
import { KPIStats } from '@/components/organisms/KPIStats';
import { TabPageLayout } from '@/components/templates/TabPageLayout';

registerLocale('fr', fr);

export const RestaurantStatsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const restaurantId = id || '';

  const defaultKpiData: RestaurantKPIResponse = {
    totalNbEvents: 0,
    showRestaurantPage: 0,
    showPartnerPopup: 0,
    showPartnerSearchRestaurantPage: 0,
    showPartnerSimilarRestaurantPage: 0,
    clickPartnerPopup: 0,
    clickPartnerSearchRestaurantPage: 0,
    clickPartnerSimilarRestaurantPage: 0,
    clickAllReviews: 0,
    clickPhone: 0,
    clickCertificate: 0,
    clickUberEats: 0,
    clickJustEat: 0,
    clickDeliveroo: 0,
    clickLinkWebsite: 0,
    clickGoogleMaps: 0
  };

  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const getPreviousMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const previousMonth = month === 0 ? 12 : month;
    const previousYear = month === 0 ? year - 1 : year;
    return `${previousYear}-${String(previousMonth).padStart(2, '0')}`;
  };

  const [selectedMonth, setSelectedMonth] = useState<string>(getPreviousMonth());
  const [startDate, setStartDate] = useState<Date | null>(new Date(`${getPreviousMonth()}-01`));
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const [year, month] = getPreviousMonth().split('-').map(Number);
    const lastDayOfMonth = new Date(year, month, 0).getDate();
    return new Date(`${year}-${String(month).padStart(2, '0')}-${lastDayOfMonth}`);
  });
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const { data: restaurant } = useFetchRestaurantQuery({ id: restaurantId, withMenu: false }, { enabled: !!restaurantId });
  const { mutate: fetchKpiData, data: kpiData, isLoading: isKpiLoading } = useFetchKPIRestaurantMutation({});
  const { mutate: fetchPeriodStatsData, data: periodStatsData, isLoading: isPeriodStatsLoading } = useFetchPeriodStatsRestaurantMutation({});
  const [showModal, setShowModal] = useState(false);

  const handleToggleModal = () => {
    setShowModal((prev) => !prev);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  useEffect(() => {
    if (restaurantId && startDate && endDate) {
      fetchPeriodStatsData({
        restaurantId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
    }
  }, [restaurantId, startDate, endDate, fetchPeriodStatsData]);

  useEffect(() => {
    if (restaurant && !restaurant.active) {
      navigate(`/restaurant/inactive/${restaurantId}`);
    }
  }, [restaurant, restaurantId, navigate]);

  const handleFetchKpiData = () => {
    if (restaurantId && startDate && endDate) {
      fetchKpiData({ restaurantId, startDate: startDate.toISOString(), endDate: endDate.toISOString() });
    }
  };

  useEffect(() => {
    handleFetchKpiData();
  }, [restaurantId, startDate, endDate]);

  const maxVisibleMonths = 5;

  useEffect(() => {
    const currentMonthIndex = months.findIndex((month) => month.value === selectedMonth);
    if (currentMonthIndex !== -1) {
      setCurrentIndex(Math.max(0, currentMonthIndex - Math.floor(maxVisibleMonths / 2)));
    }
  }, [selectedMonth, maxVisibleMonths]);

  const handleMonthSelection = (index: number) => {
    setSelectedMonth(months[index].value);
    setStartDate(new Date(`${months[index].value}-01`));
    const [year, month] = months[index].value.split('-').map(Number);
    const lastDayOfMonth = new Date(year, month, 0).getDate();
    setEndDate(new Date(`${months[index].value}-${lastDayOfMonth}`));
    setCurrentIndex(Math.max(0, index - Math.floor(maxVisibleMonths / 2)));
  };

  const totalShowRestaurantPage = periodStatsData ? periodStatsData.reduce((acc, item) => acc + Number(item.showRestaurantPage), 0) : 0;

  return (
    <>
      <TabPageLayout firstTitle="Vos stats de" secondTitle={restaurant?.name} restaurantId={restaurantId}>
        <section>
          <Typography variant="h1" weight="semibold" marginClassName="mb-10">
            Quel mois souhaitez-vous consulter ?
          </Typography>

          <MonthsDaysSelector
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            currentIndex={currentIndex}
            setCurrentIndex={setCurrentIndex}
            months={months}
            maxVisibleMonths={maxVisibleMonths}
            selectedMonth={selectedMonth}
            handleMonthSelection={handleMonthSelection}
          />
        </section>

        <section>
          <Typography variant="h1" marginClassName="mb-10">
            Vos statistiques de <b>{selectedMonth === getCurrentMonth() ? `ce mois` : months.find((month) => month.value === selectedMonth)?.label}</b>
          </Typography>

          <RestaurantCharts isLoading={isKpiLoading || isPeriodStatsLoading} data={periodStatsData} />

          <div className="flex flex-col md:flex-row w-full gap-5">
            <section className="h-fit w-full space-y-5">
              <AdStatsContainer title="Stats globales" secondtitle="sur la période" Icon={TbDeviceAnalytics}>
                <KPIDoubleSection
                  firstTitle="Affichages de votre fiche"
                  secondTitle="Interactions avec votre fiche"
                  firstKpi={totalShowRestaurantPage ?? 0}
                  secondKpi={
                    kpiData
                      ? kpiData.clickCertificate +
                        kpiData.clickAllReviews +
                        kpiData.clickGoogleMaps +
                        kpiData.clickUberEats +
                        kpiData.clickJustEat +
                        kpiData.clickDeliveroo +
                        kpiData.clickLinkWebsite
                      : 0
                  }
                  firstKpiLabel="Vues"
                  secondKpiLabel="Interactions"
                />
              </AdStatsContainer>
              <KPIStats kpiData={kpiData || defaultKpiData} />
            </section>

            <AdStatsContainer Icon={RiMegaphoneFill} title="Stats publicité">
              {restaurant?.isPartner ? (
                <>
                  <KPISection
                    title="Affichages de la pop-up publicité"
                    showKpi={kpiData?.showPartnerPopup ?? 0}
                    clickKpi={kpiData?.clickPartnerPopup ?? 0}
                    showLabel="Vues"
                    clickLabel="Clics"
                  />
                  <KPISection
                    title="Apparitions dans l'en-tête des recherches"
                    showKpi={kpiData?.showPartnerSearchRestaurantPage ?? 0}
                    clickKpi={kpiData?.clickPartnerSearchRestaurantPage ?? 0}
                    showLabel="Vues"
                    clickLabel="Clics"
                  />
                  <KPISection
                    title="Apparitions sur les restaurants concurrents"
                    showKpi={kpiData?.showPartnerSimilarRestaurantPage ?? 0}
                    clickKpi={kpiData?.clickPartnerSimilarRestaurantPage ?? 0}
                    showLabel="Vues"
                    clickLabel="Clics"
                  />
                </>
              ) : (
                <div className="space-y-5 p-4 mt-2  bg-shades-primary-active rounded-xl">
                  <Typography variant="h4" align="center" color="white" weight="bold">
                    Accès aux statistiques publicité réservé aux partenaires 🚀
                  </Typography>
                  <Typography variant="h4" align="center" color="white">
                    Boostez votre restaurant dès aujourd'hui et faites exploser vos visites !
                  </Typography>
                  <Typography variant="h4" align="center" color="white">
                    Offres limitées par ville : premier arrivé, premier servi. Ne ratez pas cette opportunité !
                  </Typography>
                  <button
                    onClick={handleToggleModal}
                    className="flex mx-auto border px-2 rounded-lg bg-primary/30  border-primary/20 items-center gap-1.5 hover:underline underline-offset-2"
                  >
                    <TbInfoCircle className="text-white text-xl" />
                    <Typography variant="h4" color="white">
                      Besoin de plus d'infos ? En savoir plus sur les publicités
                    </Typography>
                  </button>
                </div>
              )}
              <Link to={`/restaurant/subscription/${restaurantId}`}>
                <button className="text-white border-shades-primary-active py-2 duration-150 ease-in-out hover:bg-shades-primary-hover border rounded-xl p-1 text-center w-full mt-4">
                  {restaurant?.isPartner ? 'Lancer une nouvelle publicité' : 'Lancer une publicité'}
                </button>
              </Link>
            </AdStatsContainer>
          </div>
        </section>
      </TabPageLayout>
      <BaseModal maxWidth="xxl" isOpen={showModal} onCloseClick={handleModalClose}>
        <AdInfos />
      </BaseModal>
    </>
  );
};
